<template>
    <v-container>
        <v-row>
            <v-col cols="12" lg="12">
                
                <app-card title="Billing Overview">
                    <div class="pa-2">
                        <div class="pr-4 pl-4" >
                            <div style="display: flex; align-items: center; padding-top:10px">
                                <h4 class="mr-2">Account Status</h4>
                            </div>
                            <div v-if="currentBalance">
                                <span v-if="isBalancePastDue" style="color: red; margin-right:15px">You have a past due balance of {{formatMoney(currentBalance)}}.</span>
                                <span v-else style="margin-right:15px">You have an outstanding balance of {{formatMoney(currentBalance)}}.</span>
                                <app-btn @click="goToDueInvoice">
                                    Pay Now
                                </app-btn>
                            </div>
                            <div v-else>
                                <span>No invoices due or outstanding.</span>
                            </div>
                            <div style="margin-top: 15px">
                                <span style="margin-left: 25px;">• Please see <a href="#" @click="goToAllInvoices">View Invoices</a> tab for all invoices</span>
                            </div>
                        </div>
                        <v-divider class="ma-4" v-if="billingOverview.sportsPackageName"></v-divider>
                        <div v-if="billingOverview.sportsPackageName" class="pr-4 pl-4">
                            <div class="mb-4" >
                                <div style="display: flex; align-items: center">
                                    <h4 class="mr-2">Sports Billing Summary</h4>
                                </div>
                                <div>
                                    <div style="display: flex">
                                        <div style="min-width: 150px">
                                            Package Name:
                                        </div>
                                        <div>
                                            {{billingOverview.sportsPackageName}}
                                        </div>
                                    </div>
                                    <div style="display: flex">
                                        <div style="min-width: 150px">
                                            Profiles Purchased:
                                        </div>
                                        <div>
                                            {{billingOverview.sportsProfilesPurchased}}<span v-if="billingOverview.sportsProfilesFree > 0"> (including {{billingOverview.sportsProfilesFree}} free)</span>
                                        </div>
                                    </div>
                                    <div style="display: flex">
                                        <div style="min-width: 150px">
                                            Profiles Used:
                                        </div>
                                        <div>
                                            {{sportsProfilesUsed}}
                                            <v-progress-circular v-if="recalculating" indeterminate size="14" width="2" class="ml-2" />
                                        </div>
                                    </div>
                                    <div style="display: flex">
                                        <div style="min-width: 150px">
                                            Profiles Left:
                                        </div>
                                        <div>
                                            {{sportsProfilesRemaining}}
                                        </div>
                                    </div>
                                    <div style="display: flex">
                                        <div style="min-width: 150px">
                                            Next Renewal Date:
                                        </div>
                                        <div>
                                            {{formatDate(billingOverview.sportsPeriodEnd)}}
                                        </div>
                                    </div>
                                    <div v-if="shouldShowSportsProfilesTestedReport">
                                        <a href="#" @click.prevent="downloadSportsProfilesTestedReport" :class="{ disabled: downloadingSports }">
                                            {{ downloadingSports ? 'Downloading...' : 'Download Profiles Tested Report' }}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <v-divider class="ma-4" v-if="billingOverview.clinicalPackageName"></v-divider>
                        <div v-if="billingOverview.clinicalPackageName" class="pr-4 pl-4">
                            <div class="mb-4" >
                                <div style="display: flex; align-items: center">
                                    <h4 class="mr-2">Clinical Billing Summary</h4>
                                </div>
                                <div>
                                    <div style="display: flex">
                                        <div style="min-width: 150px">
                                            Package Name:
                                        </div>
                                        <div>
                                            {{billingOverview.clinicalPackageName}}
                                        </div>
                                    </div>
                                    <div style="display: flex">
                                        <div style="min-width: 150px">
                                            Profiles Purchased:
                                        </div>
                                        <div>
                                            {{billingOverview.clinicalProfilesPurchased}}<span v-if="billingOverview.clinicalProfilesFree > 0"> (including {{billingOverview.clinicalProfilesFree}} free)</span>
                                        </div>
                                    </div>
                                    <div style="display: flex">
                                        <div style="min-width: 150px">
                                            Profiles Used:
                                        </div>
                                        <div>
                                            {{clinicalProfilesUsed}}
                                            <v-progress-circular v-if="recalculating" indeterminate size="14" width="2" class="ml-2" />
                                        </div>
                                    </div>
                                    <div style="display: flex" v-if="billingOverview.isClinicalPrePaid">
                                        <div style="min-width: 150px">
                                            Profiles Left:
                                        </div>
                                        <div>
                                            {{clinicalProfilesRemaining}}
                                        </div>
                                    </div>
                                    <div style="display: flex">
                                        <div style="min-width: 150px">
                                            Next Renewal Date:
                                        </div>
                                        <div>
                                            {{formatDate(billingOverview.clinicalPeriodEnd)}}
                                        </div>
                                    </div>
                                    <div v-if="shouldShowClinicalProfilesTestedReport">
                                        <a href="#" @click.prevent="downloadClinicalProfilesTestedReport" :class="{ disabled: downloadingClinical }">
                                            {{ downloadingClinical ? 'Downloading...' : 'Download Profiles Tested Report' }}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <v-divider class="ma-4"></v-divider>
                        <div class="pr-4 pl-4">
                            <div class="mb-4" >
                                <div>
                                    To upgrade or purchase additional profiles <a href="https://www.swaymedical.com/contact" target="_blank">Contact Sway</a>
                                </div>
                                <div v-if="recalculating" style="font-size: .8em; font-style: italic; color: #757575; margin-top: 10px">
                                    Recalculating current profile usage...
                                </div>
                                <div v-else-if="usageRecalculatedOnDisplay" style="font-size: .8em; font-style: italic; color: #757575; margin-top: 10px">
                                    Profile usage last calculated on {{ usageRecalculatedOnDisplay }}.
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    

                </app-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import moment from 'moment'
import {formatMoney} from '@/js/shared/helper'
import {recalculateProfileUsage, downloadProfilesTestedReport} from '@/services/billing.service'

// find info for stripe API here
// https://stripe.com/docs/js/element/mount

export default {
    name: "BillingOverview",
    props: {
        invoices: {
            required: true
        },
        billingOverview: {
            required: true
        }
    },
    components: {
    },
    data() {
        return {
            isBalancePastDue: false,
            // Populated by recalculateProfileUsage on mount — overrides the cached
            // billingOverview.{sports,clinical}ProfilesUsed values once the recalc
            // finishes. Null until we get a response.
            freshSportsProfilesUsed: null,
            freshClinicalProfilesUsed: null,
            usageRecalculatedOn: null,
            recalculating: false,
            downloadingSports: false,
            downloadingClinical: false,
        }
    },
    async mounted() {
        await this.refreshUsage();
    },
    computed: {
        sportsProfilesUsed() {
            // Prefer the freshly-recomputed value once it's back; fall back to the
            // cached number that came in on the billingOverview prop in the meantime.
            return this.freshSportsProfilesUsed != null
                ? this.freshSportsProfilesUsed
                : this.billingOverview.sportsProfilesUsed;
        },
        clinicalProfilesUsed() {
            return this.freshClinicalProfilesUsed != null
                ? this.freshClinicalProfilesUsed
                : this.billingOverview.clinicalProfilesUsed;
        },
        usageRecalculatedOnDisplay() {
            if (!this.usageRecalculatedOn) return null;
            return moment.utc(this.usageRecalculatedOn).local().format('MMMM D, YYYY [at] h:mma');
        },
        isAdmin() {
            return this.$store.getters.isOrgAdmin;
        },
        isSuperAdmin() {
            return this.$store.getters.isSuperAdmin;
        },
        currentBalance() {
            var overdueInvoices = this.invoices.filter(x => !x.payment && x.isOverdue && !x.deletedOn && !x.isRefunded);
            if (overdueInvoices && overdueInvoices.length > 0) {
                var total = 0.0;
                for(var id in overdueInvoices){
                    total += Number(overdueInvoices[id].total.replace(/[^0-9.-]+/g,""));
                }
                this.isBalancePastDue = true;
                return total;
            }
            
            var balanceInvoices = this.invoices.filter(x => !x.payment && !x.isNotSent && !x.deletedOn && !x.isRefunded)
            if (balanceInvoices && balanceInvoices.length > 0) {
                var total = 0.0;
                for(var id in balanceInvoices){
                    total += Number(balanceInvoices[id].total.replace(/[^0-9.-]+/g,""));
                }
                this.isBalancePastDue = false;
                return total;
            }
        },
        sportsProfilesRemaining(){
            return this.billingOverview.sportsProfilesPurchased > this.sportsProfilesUsed
                    ? this.billingOverview.sportsProfilesPurchased - this.sportsProfilesUsed
                    : 0;
        },
        clinicalProfilesRemaining(){
            return this.billingOverview.clinicalProfilesPurchased > this.clinicalProfilesUsed
                    ? this.billingOverview.clinicalProfilesPurchased - this.clinicalProfilesUsed
                    : 0;
        },
        shouldShowSportsProfilesTestedReport(){
            return this.isAdmin && this.sportsProfilesUsed > 0
                    && (this.billingOverview.isProfilesTestedReportEnabled || this.isSuperAdmin)
        },
        shouldShowClinicalProfilesTestedReport(){
            return this.isAdmin && this.billingOverview.isClinicalPrePaid && this.clinicalProfilesUsed > 0
                    && (this.billingOverview.isProfilesTestedReportEnabled || this.isSuperAdmin)
        }
    },
    methods: {
        formatMoney(money) {
            return formatMoney(money, 2);
        },
        formatDate(date) {
            return moment(date).format("M/D/YYYY");
        },
        async refreshUsage() {
            // Trigger snapshot regeneration server-side, then overlay the freshly
            // computed totals onto whatever came in via the billingOverview prop.
            // Quiet on failure — the cached values remain visible as a fallback.
            this.recalculating = true;
            try {
                const response = await recalculateProfileUsage();
                if (!response) return;
                if (response.sportsUsage) {
                    this.freshSportsProfilesUsed = response.sportsUsage.profilesUsed;
                    this.usageRecalculatedOn = response.sportsUsage.lastCalculatedOn || this.usageRecalculatedOn;
                }
                if (response.clinicalUsage) {
                    this.freshClinicalProfilesUsed = response.clinicalUsage.profilesUsed;
                    this.usageRecalculatedOn = response.clinicalUsage.lastCalculatedOn || this.usageRecalculatedOn;
                }
            } finally {
                this.recalculating = false;
            }
        },
        async downloadSportsProfilesTestedReport() {
            if (this.downloadingSports)
                return;

            const id = this.billingOverview.sportsProductSubscriptionId;
            if (!id)
                return;

            this.downloadingSports = true;
            try {
                await downloadProfilesTestedReport(id);
            } finally {
                this.downloadingSports = false;
            }
        },
        async downloadClinicalProfilesTestedReport() {
            if (this.downloadingClinical)
                return;

            const id = this.billingOverview.clinicalProductSubscriptionId;
            if (!id)
                return;

            this.downloadingClinical = true;
            try {
                await downloadProfilesTestedReport(id);
            } finally {
                this.downloadingClinical = false;
            }
        },
        goToDueInvoice(){
            var invoice = this.invoices.find(x => !x.payment && !x.isNotSent)
            this.$router.push({path: '/billing/view-invoices', query: {orgId: this.$route.query.orgId, id: invoice.id}});
        },
        goToAllInvoices(){
            this.$router.push({path: '/billing/view-invoices', query: {orgId: this.$route.query.orgId}});
        }
    },
    
}
</script>

<style scoped>
a.disabled {
    pointer-events: none;
    color: #999;
}
</style>